import React, { useCallback, useEffect, useRef, useState } from 'react';
import GlassContainer from '../UI/GlassContainer';
import { usePortfolio } from '../../context/PortfolioContext';
import { processCommand } from '../../utils/terminalCommands';

// A compact terminal bar that is always visible for quick commands
const QuickTerminalBar = ({ onHelp, onNavigate }) => {
  const { portfolioData, skills, projects, experience, isAdminMode, toggleAdminMode } = usePortfolio();

  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentCommand, setCurrentCommand] = useState('');
  const [lastOutputs, setLastOutputs] = useState([]); // keep last few lines only

  const inputRef = useRef(null);

  const executeCommand = useCallback(async (command) => {
    const trimmed = command.trim();
    if (!trimmed) return;

    setCommandHistory(prev => [...prev, trimmed]);
    setHistoryIndex(-1);

    // Append the command itself to output
    setLastOutputs(prev => [
      ...prev.slice(-2),
      { id: Date.now(), type: 'command', content: `$ ${trimmed}` }
    ]);

    try {
      const result = await processCommand(trimmed.toLowerCase(), {
        portfolioData,
        skills,
        projects,
        experience,
        isAdminMode,
        toggleAdminMode,
        setCurrentSection: onNavigate || (() => {})
      });

      let text = 'Command executed';
      if (Array.isArray(result?.content)) {
        text = result.content.filter(Boolean).join(' • ');
      } else if (typeof result?.content === 'string' && result.content.trim()) {
        text = result.content;
      } else if (result?.section) {
        text = `Navigated to ${result.section}`;
      }

      setLastOutputs(prev => [
        ...prev.slice(-2),
        { id: Date.now() + 1, type: result?.type || 'system', content: text }
      ]);
    } catch (e) {
      setLastOutputs(prev => [
        ...prev.slice(-2),
        { id: Date.now() + 2, type: 'error', content: 'Command failed' }
      ]);
    }
  }, [portfolioData, skills, projects, experience, isAdminMode, toggleAdminMode, onNavigate]);

  useEffect(() => {
    // Autofocus when mounted
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (currentCommand.trim()) {
          executeCommand(currentCommand);
          setCurrentCommand('');
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (commandHistory.length > 0) {
          const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
          setHistoryIndex(newIndex);
          setCurrentCommand(commandHistory[newIndex] || '');
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (historyIndex >= 0) {
          const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : -1;
          setHistoryIndex(newIndex);
          setCurrentCommand(newIndex === -1 ? '' : commandHistory[newIndex] || '');
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="fixed left-2 sm:left-4 md:left-8 right-2 sm:right-4 md:right-8 bottom-3 sm:bottom-4 z-[60] pointer-events-none">
      <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl bg-[#09090c]/95 backdrop-blur-2xl border-2 border-white/30 shadow-[0_12px_40px_rgba(0,0,0,0.95),0_0_24px_rgba(255,255,255,0.08)] p-2.5 sm:p-3 transition-all">
        <div className="flex items-center gap-2.5 sm:gap-3 font-mono text-xs sm:text-sm">
          {/* Recent outputs */}
          {lastOutputs.length > 0 && (
            <div className="hidden lg:flex items-center gap-2 text-zinc-400 border-r border-white/20 pr-3 max-w-xs truncate">
              {lastOutputs.slice(-1).map(line => (
                <span key={line.id} className="truncate text-xs">
                  <span className={line.type === 'error' ? 'text-red-400' : 'text-emerald-400 mr-1'}>
                    {line.type === 'error' ? '!' : '❯'}
                  </span>
                  <span className="text-zinc-200">{line.content}</span>
                </span>
              ))}
            </div>
          )}

          {/* Interactive Prompt & Input */}
          <div className="flex-1 flex items-center gap-2 text-white relative min-w-0">
            <span className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-lg bg-white/10 border border-white/25 text-white font-bold text-xs shrink-0 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>shubh:~$</span>
            </span>

            <input
              ref={inputRef}
              type="text"
              value={currentCommand}
              onChange={(e) => setCurrentCommand(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type command... (try: whoami, help)"
              className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-zinc-400 text-white font-mono text-xs sm:text-sm caret-white font-medium"
            />

            {/* Quick Action Badges */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => executeCommand('whoami')}
                className="hidden sm:inline-flex px-2 py-1 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 text-zinc-200 hover:text-white text-[11px] transition-colors font-medium"
                title="Run whoami"
              >
                whoami
              </button>
              <button
                onClick={() => {
                  if (onHelp) onHelp();
                  if (onNavigate) onNavigate('help');
                  executeCommand('help');
                }}
                className="px-2.5 py-1 rounded-lg bg-white text-black border border-white hover:bg-zinc-200 text-xs font-bold transition-all shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                title="Terminal Help"
              >
                help
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickTerminalBar;


